import { useEffect, useRef, useState } from 'react';
import { diceMultiplier, DICE_TARGET_MIN } from '@raketta/shared';
import { useAppStore } from '../store/useAppStore';
import { diceApi, HistoryItem, RollResult } from '../api/dice';
import { DiceSlider } from '../components/DiceSlider';
import { Icon } from '../components/Icon';
import { telegram } from '../lib/telegram';
import { formatTon } from '../lib/format';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const ROLL_ANIM_MS = 600;

export function DiceScreen() {
  const balance = useAppStore((s) => s.balance);
  const setBalance = useAppStore((s) => s.setBalance);
  const showToast = useAppStore((s) => s.showToast);
  const openVerify = useAppStore((s) => s.openVerify);

  const [amount, setAmount] = useState('10');
  const [target, setTarget] = useState(50);
  const [rolling, setRolling] = useState(false);
  const [displayRoll, setDisplayRoll] = useState<number | null>(null);
  const [lastWin, setLastWin] = useState(false);
  const [popKey, setPopKey] = useState(0);
  const [history, setHistory] = useState<HistoryItem[]>([]);

  const animRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const multiplier = diceMultiplier(target);
  const amountNum = parseFloat(amount) || 0;
  const balanceNum = parseFloat(balance) || 0;
  const profit = amountNum * multiplier - amountNum;
  const canRoll = !rolling && amountNum > 0 && amountNum <= balanceNum && target >= DICE_TARGET_MIN;

  useEffect(() => {
    diceApi.history(15).then(setHistory).catch(() => undefined);
    return () => {
      if (animRef.current) clearInterval(animRef.current);
    };
  }, []);

  const setAmountSafe = (v: number) => {
    const clamped = Math.max(0, Math.min(v, balanceNum || v));
    setAmount(clamped % 1 === 0 ? String(clamped) : clamped.toFixed(2));
  };

  const roll = async () => {
    if (!canRoll) {
      if (amountNum > balanceNum) showToast('Недостаточно средств', true);
      return;
    }
    telegram.haptic('medium');
    setRolling(true);

    animRef.current = setInterval(() => {
      const r = Math.random() * 100;
      setDisplayRoll(r);
      setLastWin(r < target);
    }, 45);

    const start = performance.now();
    try {
      const res: RollResult = await diceApi.rollDice(amount, target);
      const elapsed = performance.now() - start;
      if (elapsed < ROLL_ANIM_MS) await sleep(ROLL_ANIM_MS - elapsed);

      if (animRef.current) clearInterval(animRef.current);
      setDisplayRoll(res.roll);
      setLastWin(res.win);
      setPopKey((k) => k + 1);
      setBalance(res.newBalance);
      telegram.notify(res.win ? 'success' : 'error');

      setHistory((h) =>
        [
          {
            betId: res.betId,
            amount: res.amount,
            payout: res.payout,
            multiplier: String(res.multiplier),
            nonce: res.nonce,
            result: { roll: res.roll, target: res.target, win: res.win, multiplier: res.multiplier },
            createdAt: res.createdAt,
          } as HistoryItem,
          ...h,
        ].slice(0, 15)
      );

      if (res.win) showToast(`Выигрыш +${formatTon(res.payout)}`);
    } catch (e: any) {
      if (animRef.current) clearInterval(animRef.current);
      const msg = e?.response?.data?.message || 'Ошибка ставки';
      showToast(Array.isArray(msg) ? msg[0] : msg, true);
    } finally {
      setRolling(false);
    }
  };

  return (
    <>
      {/* Последние результаты */}
      {history.length > 0 && (
        <div className="recent">
          {history.map((h) => (
            <button
              key={h.betId}
              className={`pill ${h.result.win ? 'win' : 'lose'}`}
              onClick={() => openVerify(h.betId)}
              title="Проверить честность"
            >
              {h.result.roll.toFixed(2)}
            </button>
          ))}
        </div>
      )}

      {/* Игровое поле */}
      <div className="card dice-board">
        <DiceSlider
          target={target}
          onChange={setTarget}
          disabled={rolling}
          lastRoll={displayRoll}
          lastWin={lastWin}
          popKey={popKey}
        />

        <div className="stats">
          <div className="stat">
            <div className="label">Множитель</div>
            <div className="value accent">{multiplier.toFixed(2)}×</div>
          </div>
          <div className="stat">
            <div className="label">Меньше чем</div>
            <div className="value">{target.toFixed(2)}</div>
          </div>
          <div className="stat">
            <div className="label">Шанс</div>
            <div className="value">{target.toFixed(2)}%</div>
          </div>
        </div>
      </div>

      {/* Ставка */}
      <div className="card bet">
        <div>
          <div className="label-row">
            <span className="label">Сумма ставки</span>
          </div>
          <div className="field">
            <span className="gem">
              <Icon name="gem" size={16} />
            </span>
            <input
              inputMode="decimal"
              value={amount}
              disabled={rolling}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="0"
            />
            <div className="mini-actions">
              <button onClick={() => setAmountSafe(amountNum / 2)} disabled={rolling}>
                ½
              </button>
              <button onClick={() => setAmountSafe(amountNum * 2)} disabled={rolling}>
                2×
              </button>
              <button onClick={() => setAmountSafe(balanceNum)} disabled={rolling}>
                MAX
              </button>
            </div>
          </div>
        </div>

        <div>
          <div className="label-row">
            <span className="label">Профит при выигрыше</span>
          </div>
          <div className="field readonly">
            <span className="gem">
              <Icon name="gem" size={16} />
            </span>
            <input value={formatTon(profit)} readOnly tabIndex={-1} />
          </div>
        </div>

        <button className={`btn ${rolling ? 'loading' : ''}`} disabled={!canRoll} onClick={roll}>
          {rolling ? <span className="spin" /> : 'Сделать ставку'}
        </button>
      </div>
    </>
  );
}
